import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";

import { Container, Row, Col, Button, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { FiSearch } from "react-icons/fi";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { toast } from "react-toastify";
import { STUDENT_CERTIFICATE_API } from "../../../api/api";

import lockIconUrl from "../../../assets/icons/student/lock-certifictae.svg";
// import StudentcertificateCard from "../../../assets/student/no-certificates.png";
import NotFoundCertificate from "../../../assets/icons/student/certificate.svg";
import CertificateBG from "../../../assets/student/student-certificate.png";
import Logo from "../../../assets/Skilledition-logo.png";
import Signature from "../../../assets/student/signature.png";

import StudentDummyCertificate from "../../../assets/student/student-dummy-certificate.png";

import { handleApiError } from "../../../utils/apiErrorHandler";

import "./StudentCertificate.css";

const StudentCertificate = () => {
  const navigate = useNavigate();
  const certificateRef = useRef();

  const [certificates, setCertificates] = useState([]);
  const [certificateStats, setCertificateStats] = useState({});
  const [selectedCertificate, setSelectedCertificate] = useState(null);

  const [activeTab, setActiveTab] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");

  //loading
  const [loadingAllCertificate, setAllCertificateLoading] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("user_id");

    if (!userId || !token) {
      setAllCertificateLoading(false);
      navigate("/signin");

      return;
    }

    const fetchCertificates = async () => {
      try {
        const response = await axios.post(
          STUDENT_CERTIFICATE_API.GET_STUDENT_CERTIFICATES,
          { student_id: userId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = response.data;

        if (data.statusCode === 200) {
          const progressCourses = data.progress || [];
          const issuedCertificates = data.certificates_data || [];

          let certificateList = [];

          // Pending courses
          progressCourses.forEach((course) => {
            certificateList.push({
              courseId: course.course_id,
              title: course.course_title,
              certificateId: null,
              issuedOn: null,
              studentName: "",
              status: "Pending",
            });
          });

          // Issued certificates
          issuedCertificates.forEach((cert) => {
            certificateList.push({
              courseId: cert.course_id,
              title: cert.course_title,
              certificateId: cert.certificate_number,
              issuedOn: cert.issued_at,
              studentName: cert.full_name,
              status: "Unlocked",
            });
          });

          setCertificates(certificateList);
          setCertificateStats(data.stats || {});
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching certificates:", error);
        // Server responded with non-2xx status (like 400, 500)

        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/signin");
      } finally {
        setAllCertificateLoading(false);
      }
    };

    fetchCertificates();
  }, [navigate]);

  /* FILTER LOGIC */
  const filteredCertificates = useMemo(() => {
    return certificates
      .filter((cert) => {
        if (activeTab === "COMPLETED") return cert.status === "Unlocked";
        if (activeTab === "PENDING") return cert.status === "Pending";
        return true;
      })
      .filter((cert) =>
        (cert.title || "")
          .toLowerCase()
          .includes(searchTerm.trim().toLowerCase()),
      );
  }, [certificates, activeTab, searchTerm]);

  const handleDownload = async (cert) => {
    const element = certificateRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("landscape", "px", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    pdf.addImage(imgData, "PNG", 0, 0, pageWidth, pageHeight);
    const safeName = cert.title.replace(/[^a-z0-9]/gi, "_");
    pdf.save(`${safeName}.pdf`);
  };

  const handleDownloadClick = (cert) => {
    setSelectedCertificate(cert);

    setTimeout(() => {
      handleDownload(cert);
    }, 100);
  };

  const completedCount = useMemo(
    () => certificates.filter((cert) => cert.status === "Unlocked").length,
    [certificates],
  );
  const pendingCount = useMemo(
    () => certificates.filter((cert) => cert.status === "Pending").length,
    [certificates],
  );

  if (loadingAllCertificate) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }
  return (
    <Container fluid className="student-certificate-section-container">
      {/* HEADER */}
      <Row className="align-items-center mb-3">
        <h2 className="student-certificate-section-title">Certificates</h2>
        <p className="student-certificate-section-description">
         Celebrate your achievements with certificates that recognize your learning. 
        </p>
      </Row>

      <div className="student-certificate-banners">
        <Row className="student-certificate-banners-row">
          <Col className="student-certificate-banners-item">
            <p className="student-certificate-banners-label">
              Certificates Earned
            </p>
            <h2 className="student-certificate-banners-value">
              {certificateStats.certificates_earned || "-"}
            </h2>
          </Col>

          <Col className="student-certificate-banners-item">
            <p className="student-certificate-banners-label">
              Courses Completed
            </p>
            <h2 className="student-certificate-banners-value">
              {certificateStats.courses_completed || "-"}
            </h2>
          </Col>

          <Col className="student-certificate-banners-item">
            <p className="student-certificate-banners-label">
              Purchased Courses
            </p>
            <h2 className="student-certificate-banners-value">
              {certificateStats.purchased_courses || "-"}
            </h2>
          </Col>
        </Row>
      </div>

      {/* TABS */}
      <div className="student-certificate-tabs student-certificate-card-tab">
        <Row className="align-items-center">
          <Col md={8}>
            <div className="student-certificate-tabs-buttons">
              <button
                className={activeTab === "ALL" ? "active" : ""}
                onClick={() => setActiveTab("ALL")}
              >
                All Certificates ({certificates.length})
              </button>

              <button
                className={activeTab === "COMPLETED" ? "active" : ""}
                onClick={() => setActiveTab("COMPLETED")}
              >
                {/* Course Certificates ({completedCount}) */}
                Issued Certificates ({completedCount})
              </button>

              <button
                className={activeTab === "PENDING" ? "active" : ""}
                onClick={() => setActiveTab("PENDING")}
              >
                {/* In Progress ({pendingCount}) */}
                Pending Certificates ({pendingCount})
              </button>
            </div>
          </Col>

          {/* SEARCH */}
          <Col md={4} className="d-flex justify-content-end">
            <div className="student-slider-header-search">
              <input
                type="text"
                placeholder="Search by course"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FiSearch className="student-slider-search-icon" />
            </div>
          </Col>
        </Row>
      </div>

      {filteredCertificates.length > 0 ? (
        <Row>
          {filteredCertificates.map((cert) => (
            <Col
              key={cert.courseId}
              lg={4}
              md={6}
              sm={12}
              className="student-certificate-section-col"
            >
              <div className="student-certificate-section-card">
                {/* IMAGE */}
                <div className="student-certificate-section-image-container">
                  <img
                    src={StudentDummyCertificate}
                    alt={cert.title}
                    className={`student-certificate-section-image ${cert.status === "Unlocked" ? "unlocked" : ""}`}
                  />

                  {cert.status === "Pending" && (
                    <div className="student-certificate-section-overlay">
                      <img
                        src={lockIconUrl}
                        alt="Locked"
                        className="student-certificate-section-lock-icon"
                      />
                    </div>
                  )}
                </div>

                {/* INFO */}
                <div className="student-certificate-section-info">
                  <div className="d-flex justify-content-between">
                    <h5 className="student-certificate-section-title-text">
                      {cert.title}
                    </h5>

                    <span
                      className={`student-certificate-section-status-badge ${
                        cert.status === "Unlocked"
                          ? "student-certificate-section-badge-unlocked"
                          : "student-certificate-section-badge-pending"
                      }`}
                    >
                      {cert.status}
                    </span>
                  </div>

                  <p className="student-certificate-section-date">
                    Issued on :{" "}
                    <strong className="student-certificate-section-date-strong">
                      {cert.issuedOn || "—"}
                    </strong>
                  </p>

                  <p className="student-certificate-section-id">
                    Certificate ID :{" "}
                    <strong className="student-certificate-section-date-strong">
                      {cert.certificateId || "—"}
                    </strong>
                  </p>
                </div>

                {/* BUTTON */}
                <Button
                  disabled={cert.status !== "Unlocked"}
                  className="student-certificate-section-btn-download"
                  onClick={() => handleDownloadClick(cert)}
                >
                  Download
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      ) : (
        <div className="student-certificate-empty-box mt-2">
          <img
            src={NotFoundCertificate}
            alt="No Certificates"
            className="student-certificate-empty-icon"
          />
          <h5 className="student-certificate-empty-title">
            No Certificates Available
          </h5>
          <p className="student-certificate-empty-desc">
            You don't have any certificates yet.
          </p>
          {/* <Button
            className="student-certificate-empty-btn"
            onClick={() => navigate("/student/courses")}
          >
            View Available Courses
          </Button> */}
        </div>
      )}

      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
        <div ref={certificateRef} className="certificate-wrapper">
          <img
            src={CertificateBG}
            alt="certificate"
            className="certificate-bg"
          />

          <div className="certificate-overlay">
            <img src={Logo} alt="logo" className="certificate-logo" />

            <h2 className="certificate-main-title">CERTIFICATE</h2>
            <p className="certificate-subtitle">OF ACHIEVEMENT</p>

            <p className="certificate-presented">PROUDLY PRESENTED TO</p>

            <h1 className="certificate-name">
              {selectedCertificate?.studentName}
            </h1>

            <p className="certificate-course">{selectedCertificate?.title}</p>

            <p className="certificate-date">{selectedCertificate?.issuedOn}</p>

            <div className="certificate-footer">
              <div className="signature-block">
                <p className="signature-title">
                  This certificate was awarded by :
                </p>
                <div className="signature-line">
                  <img src={Signature} alt="signature" />
                </div>
                <p className="signature-title">Managing Director</p>
              </div>
            </div>

            {/* Certificate ID */}
            <p className="certificate-id">
              Certificate ID: {selectedCertificate?.certificateId}
            </p>
          </div>
        </div>
      </div>
    </Container>
  );
};

export default StudentCertificate;
